import { NavLink, useNavigate } from "react-router-dom";
import { FlaskConical, Gift, X } from "lucide-react";

import { useBootstrap } from "@/lib/api/hooks";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Icon as BrandIcon } from "@/lib/Icon";
import { cn } from "@/lib/cn";
import { useLabsExperiment, useLabEvent } from "@/features/labs/labExperiment";
import { supportChat } from "./support/supportChatUi";
import { useClinicNavUnread } from "@/features/messaging/useMessagingUnread";

/**
 * OverflowDrawer — full-screen mobile menu opened from the bottom-nav `⋯`.
 * Lists every patient route (Home, Plan, Orders, Clinic, Learn, Upgrade,
 * Account) with the System Labs heading + the Need Help? footer (email +
 * phone, pulled from /me/bootstrap).
 *
 * Mirrors the mock: monospace caps title with a close X, each row is
 * icon + label + ▶ chevron, light grey hover.
 */

type NavIconKey =
  | "home"
  | "stack"
  | "clinic"
  | "orders"
  | "learn"
  | "upgrade"
  | "account"
  | "referral"
  | "labs";

// Home keeps the lucide stroke icon (no brand SVG exists for it); the
// rest pull from /public/icons/. Order mirrors the desktop Sidebar.
const ITEMS: { to: string; label: string; icon: NavIconKey }[] = [
  { to: "/app", label: "Home", icon: "home" },
  { to: "/app/protocols", label: "Stack", icon: "stack" },
  { to: "/app/labs", label: "Labs", icon: "labs" as const },
  { to: "/app/clinic", label: "Clinic", icon: "clinic" },
  { to: "/app/orders", label: "Orders", icon: "orders" },
  { to: "/app/learn", label: "Learn", icon: "learn" },
  { to: "/app/shop", label: "Shop", icon: "upgrade" },
  { to: "/app/account", label: "Account", icon: "account" },
];

// Gated behind the `gift-referrals` experiment — appended only for the cohort.
const REFERRAL_ITEM: { to: string; label: string; icon: NavIconKey } = {
  to: "/app/referrals",
  label: "Referrals",
  icon: "referral",
};

function NavIcon({
  kind,
  className,
}: {
  kind: NavIconKey;
  className?: string;
}) {
  if (kind === "home") {
    return (
      <span
        className={cn("inline-flex items-center justify-center", className)}
      >
        <BrandIcon name="Mark Line" viewBox="0 0 32 30" />
      </span>
    );
  }
  if (kind === "labs") return <FlaskConical className={className} size={16} strokeWidth={1.5} aria-hidden />;
  if (kind === "referral") {
    return (
      <span
        className={cn("inline-flex items-center justify-center", className)}
      >
        <Gift size={16} strokeWidth={1.5} />
      </span>
    );
  }
  return (
    <img
      src={`/icons/icon-${kind}.svg`}
      alt=""
      aria-hidden
      className={cn("object-contain", className)}
    />
  );
}

export function OverflowDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { enabled: labsEnabled } = useLabsExperiment();
  const emitLab = useLabEvent();
  const { data: bootstrap } = useBootstrap();
  const { signOut } = useAuth();
  // Referrals + in-house AI support rolled out to 100% — the gift-referrals
  // and inhouse-chat experiments completed 2026-07-13 with variant-a winning.
  // Referrals sits just above Account (the last item).
  const items: typeof ITEMS = [...ITEMS.slice(0, -1).filter((item) => item.icon !== "labs" || labsEnabled), REFERRAL_ITEM, ITEMS[ITEMS.length - 1]];
  const navigate = useNavigate();

  async function handleSignOut() {
    if (!window.confirm("Sign out of System Labs?")) return;
    await signOut();
    onClose();
    navigate("/auth", { replace: true });
  }

  const clinicUnread = useClinicNavUnread();
  const unreadByPath: Record<string, number> = {
    "/app/clinic": clinicUnread,
  };

  return (
    <>
      {/* Scrim — tapping closes. */}
      {open && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="md:hidden fixed inset-0 bg-black/10 backdrop-blur-[10px] z-helpdock"
        />
      )}
      <aside
        aria-hidden={!open}
        className={cn(
          "md:hidden fixed inset-x-0 top-45 bottom-0 z-drawer bg-[var(--surface-page)] flex flex-col",
          "transition-transform duration-base ease-ease",
          open ? "translate-y-0" : "translate-y-full",
        )}
      >
        <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle pr-2">
          <h2 className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">
            System Laboratories, Inc.
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-45 h-45 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        <nav className="flex-1 overflow-y-auto">
          <ul>
            {items.map((item) => (
              <li
                key={item.to}
                className={cn("border-b border-line-subtle",
                )}
              >
                <NavLink
                  to={item.to}
                  end={item.to === "/app"}
                  onClick={() => { if (item.to === "/app/labs") emitLab("blood_test_labs_clicked", { source: "mobile_menu" }); onClose(); }}
                  data-tour={item.to === "/app/clinic" ? "clinic-row" : undefined}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-15 pr-20 type-body-lg",
                      isActive
                        ? "bg-[var(--surface-sunken)] text-ink"
                        : "text-ink bg-[var(--surface-raised)]",
                    )
                  }
                >
                  <div className="w-[5rem] h-[5rem] bg-sunken flex items-center justify-center">
                    <NavIcon
                      kind={item.icon}
                      className={`${item.icon === "home" ? "w-18 h-18" : "w-16 h-16"} text-ink-secondary shrink-0`}
                    />
                  </div>
                  <span className="flex-1">{item.label}</span>
                  {(unreadByPath[item.to] ?? 0) > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-18 h-18 text-12 px-5 rounded-pill bg-accent text-white type-data-xs"
                      aria-label={`${unreadByPath[item.to]} unread`}
                    >
                      {unreadByPath[item.to]}
                    </span>
                  )}
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center w-8 text-ink-muted shrink-0"
                  >
                    <BrandIcon name="Arrow" viewBox="0 0 10 12" />
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="px-15 pt-15 pb-5">
          <button
            type="button"
            onClick={handleSignOut}
            className="type-label-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline"
          >
            Sign out
          </button>
        </div>

        <footer className="border-t border-line-subtle px-15 py-20">
          <div className="type-label-md text-ink mb-10">NEED HELP?</div>
          <p className="type-body-sm text-ink-secondary leading-relaxed">
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-8 h-8 text-ink-muted mr-5 align-middle"
            >
              <BrandIcon name="Arrow" viewBox="0 0 10 12" />
            </span>
            <button
              type="button"
              onClick={() => {
                onClose();
                supportChat.open({ chooser: true });
              }}
              className="underline underline-offset-4 text-ink"
            >
              Chat with us
            </button>{" "}
            or contact our clinical team via email{" "}
            <a
              href={`mailto:${bootstrap?.help_contact?.email ?? "support@systemlabs.com"}`}
              className="underline underline-offset-4 text-ink"
            >
              {bootstrap?.help_contact?.email ?? "support@systemlabs.com"}
            </a>{" "}
            or text us at{" "}
            <a
              href={`tel:${(bootstrap?.help_contact?.phone ?? "+1 (949) 268-9869").replace(/[^\d+]/g, "")}`}
              className="underline underline-offset-4 text-ink"
            >
              {bootstrap?.help_contact?.phone ?? "(949) 268-9869"}
            </a>
            .
          </p>
        </footer>
      </aside>
    </>
  );
}
